import apiClient from '@/lib/api-client';
import { ApiError } from '@/model/ApiError';
import {
    Credential,
    TokenRequest,
    LoginFormData,
    LoginRequest,
    LoginResponse,
} from '@/model/Authentication';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';
import { parseToken } from '@/lib/helper/token-helper';
import { ProfileFormData, User } from '@/model/Profile';
import router from '@/router';

@Module({ namespaced: true, preserveState: true })
export default class AuthenticationStorage extends VuexModule {
    credential: Credential | null = null;

    error: ApiError | null = null;

    errorProfile: ApiError | null = null;

    user: User | null = null;

    @Mutation
    setCredential(loginResponse: LoginResponse | null): void {
        if (loginResponse) {
            const accessTokenJwtPayload = parseToken(
                loginResponse?.accessToken,
            );

            const refreshTokenJwtPayload = parseToken(
                loginResponse?.refreshToken,
            );

            this.credential = {
                ...loginResponse,
                accessTokenExpiration: accessTokenJwtPayload.exp * 1000,
                refreshTokenExpiration: refreshTokenJwtPayload.exp * 1000,
            };
        } else {
            this.credential = null;
        }
    }

    @Mutation
    setErrorProfile(errorProfile: ApiError | null): void {
        this.errorProfile = errorProfile;
    }

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setUser(newUser: User | null): void {
        this.user = newUser;
    }

    @Mutation
    setUserFirstNameLastNamePhone(formData: ProfileFormData): void {
        if (this.user) {
            this.user.firstName = formData.firstName;
            this.user.lastName = formData.lastName;
            this.user.phone = formData.phone;
        }
    }

    @Action
    login(formData: LoginFormData): Promise<void> {
        return apiClient
            .post<LoginRequest, LoginResponse>('/auth/login', formData)
            .then((data) => {
                this.context.commit('setCredential', data);
                this.context.dispatch('fetchUser');
                return Promise.resolve();
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject(error);
            });
    }

    @Action
    refresh(): Promise<void> {
        return apiClient
            .post<TokenRequest, LoginResponse>('/auth/refresh', {
                token: this.credential?.refreshToken,
            })
            .then((data) => {
                this.context.commit('setCredential', data);
            })
            .catch(() => {
                this.context.commit('setCredential', null);
                router.push({ name: 'Login' });
            });
    }

    @Action
    logout(): Promise<void> {
        return apiClient.post<void, void>('/auth/logout').finally(() => {
            this.context.commit('setCredential', null);
        });
    }

    @Action
    fetchUser(): Promise<void> {
        return apiClient
            .get('/users/me')
            .then((data) => {
                this.context.commit('setUser', data);
                return Promise.resolve();
            })
            .catch((error) => {
                this.context.commit('setErrorProfile', error);
                return Promise.reject();
            });
    }

    get getCredential(): Credential | null {
        return this.credential;
    }

    get isAuthenticated(): boolean {
        return !!this.credential?.accessToken;
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getUser(): User | null {
        return this.user;
    }

    get getErrorProfile(): ApiError | null {
        return this.errorProfile;
    }
}
